import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiErrorDetail } from '../../lib/adminApi';
import { ImageUpload, GalleryUpload } from './ImageUpload';

const EMPTY = { couple: '', date: '', venue: '', description: '', image: '', gallery: [], full_story: '', featured: false };
const inputCls = 'w-full border border-[#E6E2DC] px-4 py-2.5 text-sm focus:outline-none focus:border-[#DE92A9] bg-white';
const labelCls = 'block text-xs tracking-wider uppercase text-[#757B7F] mb-1.5';

export default function WeddingsManager() {
  const [weddings, setWeddings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/api/weddings').then((res) => setWeddings(res.data)).catch(() => toast.error('Failed to load weddings'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (w) => {
    setForm({ couple: w.couple, date: w.date, venue: w.venue, description: w.description, image: w.image, gallery: w.gallery || [], full_story: w.full_story || '', featured: !!w.featured });
    setEditing(w);
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error('Please upload a cover image'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/api/admin/weddings', form);
        toast.success('Wedding story created');
      } else {
        await api.put(`/api/admin/weddings/${editing.slug || editing.id}`, form);
        toast.success('Wedding story updated');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete "${w.couple}" permanently?`)) return;
    try {
      await api.delete(`/api/admin/weddings/${w.slug || w.id}`);
      toast.success('Wedding story deleted');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Delete failed');
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="bg-white border border-[#E6E2DC]/60 p-8 max-w-3xl space-y-5" data-testid="wedding-form">
        <h2 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: '"Playfair Display", serif' }}>
          {editing === 'new' ? 'New Wedding Story' : `Edit: ${editing.couple}`}
        </h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Couple *</label>
            <input className={inputCls} value={form.couple} onChange={set('couple')} required placeholder="Anna & Mark" data-testid="wedding-couple-input" />
          </div>
          <div>
            <label className={labelCls}>Date *</label>
            <input className={inputCls} value={form.date} onChange={set('date')} required placeholder="12 September 2026" data-testid="wedding-date-input" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Venue *</label>
          <input className={inputCls} value={form.venue} onChange={set('venue')} required placeholder="Palazzo Parisio, Naxxar" data-testid="wedding-venue-input" />
        </div>
        <div>
          <label className={labelCls}>Short description *</label>
          <textarea className={inputCls} rows={3} value={form.description} onChange={set('description')} required data-testid="wedding-description-input" />
        </div>
        <ImageUpload label="Cover image *" value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="wedding-cover-upload" />
        <GalleryUpload value={form.gallery} onChange={(g) => setForm({ ...form, gallery: g })} testId="wedding-gallery-upload" />
        <div>
          <label className={labelCls}>Full story (plain text or HTML)</label>
          <textarea className={`${inputCls} font-mono text-xs`} rows={12} value={form.full_story} onChange={set('full_story')} data-testid="wedding-story-input" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#2C2C2C]">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="wedding-featured-checkbox" />
          Featured on homepage
        </label>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="text-white text-xs tracking-[0.15em] font-semibold px-8 py-3 hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#DE92A9' }} data-testid="wedding-save-btn">
            {saving ? 'SAVING...' : 'SAVE STORY'}
          </button>
          <button type="button" onClick={() => setEditing(null)} className="border border-[#E6E2DC] text-xs tracking-wider px-6 py-3 text-[#757B7F] hover:border-[#DE92A9]" data-testid="wedding-cancel-btn">
            CANCEL
          </button>
        </div>
      </form>
    );
  }

  return (
    <div data-testid="weddings-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: '"Playfair Display", serif' }}>Wedding Stories</h2>
        <button onClick={openNew} className="inline-flex items-center gap-2 text-white text-xs tracking-wider font-semibold px-5 py-2.5 hover:opacity-90" style={{ backgroundColor: '#DE92A9' }} data-testid="wedding-add-btn">
          <Plus size={14} /> ADD STORY
        </button>
      </div>
      <div className="bg-white border border-[#E6E2DC]/60 divide-y divide-[#E6E2DC]/60">
        {weddings.map((w) => (
          <div key={w.id} className="flex items-center gap-4 p-4" data-testid={`wedding-row-${w.slug}`}>
            <img src={w.image} alt={w.couple} className="w-20 h-14 object-cover border border-[#E6E2DC] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#2C2C2C] font-medium flex items-center gap-1.5">
                {w.couple}
                {w.featured && <Star size={12} className="text-[#DE92A9] fill-[#DE92A9]" />}
              </p>
              <p className="text-xs text-[#757B7F] truncate">{w.date} · {w.venue}</p>
            </div>
            <button onClick={() => openEdit(w)} className="p-2 text-[#757B7F] hover:text-[#DE92A9]" data-testid={`wedding-edit-${w.slug}`} aria-label="Edit"><Pencil size={15} /></button>
            <button onClick={() => remove(w)} className="p-2 text-[#757B7F] hover:text-red-500" data-testid={`wedding-delete-${w.slug}`} aria-label="Delete"><Trash2 size={15} /></button>
          </div>
        ))}
        {weddings.length === 0 && <p className="p-6 text-sm text-[#757B7F]">No wedding stories yet.</p>}
      </div>
    </div>
  );
}
